import React, { createContext, useContext, useState } from 'react';
import { InteractiveTour, useInteractiveTour } from '@/components/InteractiveTour';

interface ReviewTourContextType {
  startReviewTour: () => void;
  startFieldValidationTour: () => void;
  startPDFNavigationTour: () => void;
  isOpen: boolean;
  closeTour: () => void;
}

const ReviewTourContext = createContext<ReviewTourContextType | undefined>(undefined);

interface ReviewTourProviderProps {
  children: React.ReactNode;
}

// Enhanced tour definitions for review interface
const reviewTours = [
  {
    id: 'review-interface',
    name: 'Review Interface',
    description: 'Learn how to review and validate bills',
    category: 'features' as const,
    steps: [
      {
        id: '1',
        title: 'Welcome to Review Interface',
        content: 'This is where you review and validate processed bills. The interface is split into a PDF viewer and validation panel.',
        target: '[data-tour="review-interface"]',
        position: 'bottom' as const
      },
      {
        id: '2',
        title: 'PDF Viewer',
        content: 'View the original bill document here. You can zoom in/out and navigate between pages.',
        target: '[data-tour="pdf-viewer"]',
        position: 'right' as const
      },
      {
        id: '3',
        title: 'Customer Assignment',
        content: 'Assign the bill to the correct customer. This is a critical step for proper routing.',
        target: '[data-tour="customer-selection"]',
        position: 'left' as const
      },
      {
        id: '4',
        title: 'Field Validation',
        content: 'Review and correct extracted data. Red fields indicate validation errors that need attention.',
        target: '[data-tour="field-validation"]',
        position: 'left' as const
      },
      {
        id: '5',
        title: 'Validation Progress',
        content: 'Track your validation progress. Aim for 100% completion before finishing the review.',
        target: '[data-tour="validation-progress"]',
        position: 'left' as const
      },
      {
        id: '6',
        title: 'Save and Complete',
        content: 'Save your progress regularly. When all fields are validated, click Complete Review.',
        target: '[data-tour="review-actions"]',
        position: 'top' as const
      }
    ]
  },
  {
    id: 'field-validation',
    name: 'Field Validation',
    description: 'Master the field validation process',
    category: 'features' as const,
    steps: [
      {
        id: '1',
        title: 'Field Types',
        content: 'Different field types require different validation. Amount fields need numbers, dates need proper format.',
        target: '[data-tour="field-types"]',
        position: 'right' as const
      },
      {
        id: '2',
        title: 'Error Indicators',
        content: 'Red borders indicate validation errors. Hover over fields to see specific error messages.',
        target: '[data-tour="field-errors"]',
        position: 'right' as const
      },
      {
        id: '3',
        title: 'Auto-suggestions',
        content: 'Many fields provide auto-suggestions based on historical data. Use them to speed up validation.',
        target: '[data-tour="field-suggestions"]',
        position: 'right' as const
      },
      {
        id: '4',
        title: 'Required Fields',
        content: 'Fields marked with * are required. You cannot complete the review without filling these.',
        target: '[data-tour="required-fields"]',
        position: 'right' as const
      }
    ]
  },
  {
    id: 'pdf-navigation',
    name: 'PDF Navigation',
    description: 'Navigate and interact with PDF documents',
    category: 'features' as const,
    steps: [
      {
        id: '1',
        title: 'Zoom Controls',
        content: 'Use zoom controls to get a better view of the document. Ctrl + and Ctrl - also work.',
        target: '[data-tour="pdf-zoom"]',
        position: 'bottom' as const
      },
      {
        id: '2',
        title: 'Page Navigation',
        content: 'Navigate between pages using the arrow buttons or left/right arrow keys.',
        target: '[data-tour="pdf-pages"]',
        position: 'bottom' as const
      },
      {
        id: '3',
        title: 'Scroll and Pan',
        content: 'Use mouse wheel to scroll, or click and drag to pan around large documents.',
        target: '[data-tour="pdf-viewer"]',
        position: 'right' as const
      },
      {
        id: '4',
        title: 'Mobile Gestures',
        content: 'On mobile devices, use pinch to zoom and swipe to navigate between pages.',
        target: '[data-tour="pdf-viewer"]',
        position: 'right' as const
      }
    ]
  }
];

export function ReviewTourProvider({ children }: ReviewTourProviderProps) {
  const { isOpen, currentTourId, startTour, closeTour } = useInteractiveTour();

  const startReviewTour = () => {
    startTour('review-interface');
  };

  const startFieldValidationTour = () => {
    startTour('field-validation');
  };

  const startPDFNavigationTour = () => {
    startTour('pdf-navigation');
  };

  return (
    <ReviewTourContext.Provider value={{
      startReviewTour,
      startFieldValidationTour,
      startPDFNavigationTour,
      isOpen,
      closeTour
    }}>
      {children}
      <InteractiveTour 
        isOpen={isOpen} 
        onClose={closeTour} 
        tourId={currentTourId}
      />
    </ReviewTourContext.Provider>
  );
}

export function useReviewTour() {
  const context = useContext(ReviewTourContext);
  if (context === undefined) {
    throw new Error('useReviewTour must be used within a ReviewTourProvider');
  }
  return context;
}